/**
 * exam-bank/practice — 「📝 真題練習」入口畫面(卷清單)。
 *
 * 流程:loadExamBanks() → 依科分組列出所有「卷」+ 英文單字聽寫 →
 *   選一份卷 → renderExamPaper(整張段考卷排版,不再一問一答)→ 返回卷清單。
 *   選單字聽寫 → toDictationItems → 重用既有 runDictation。
 *
 * 視覺 = 學術風格設計系統(boss 2026-06-08;見 wiki she-ji-xi-tong / page-470f59c0)。
 * 卷清單套紙底頁殼(`.ef-page` + serif「真題練習」標題),每份卷 / 聽寫用學術
 * 紙片卡(`academicCard`)。escape 生命週期維持原樣(本畫面自管一顆,進考卷 /
 * 聽寫前先卸、回清單再 remount),避免同時兩顆 ✕。
 *
 * 老闆回饋(2026-06-07):真題練習要像真實段考卷的排版(一次看整張、分大題、
 * 題號、選項/作答格都在版面上),不要一問一答。卷清單保留;「選一份卷之後」
 * 改走 renderExamPaper。
 *
 * 學習介面要專業(boss 2026-05-23):純文字、極簡卡片,無音效、無 mascot、
 * 無彩光、文案中性。學習/遊戲分離鐵則:純學習側,絕不碰 commanderXp / 戰鬥數值。
 */

import { mountEscapeButton } from '../escape-button';
import { ensureAcademicTheme } from '../ui/theme';
import { academicCard } from '../ui/components';
import { runDictation } from '../review/dictation';
import { toDictationItems } from '../../shared/vocab-store';
import { renderExamPaper } from './paper-view';
import {
  loadExamBanks,
  isAutoGraded,
  type ExamPaperSummary,
  type ExamPaperEntry,
  type VocabSetEntry,
} from './loader';
import type { Subject } from '../../shared/types';

const SUBJECT_ORDER: Subject[] = ['math', 'chinese', 'english', 'nature', 'social'];
const SUBJECT_LABEL: Record<Subject, string> = {
  math: '數學',
  chinese: '國語',
  english: '英文',
  nature: '自然',
  social: '社會',
};

/**
 * Render 真題練習 into `container`. `onDone` 由 ✕ 離開觸發回上層。
 */
export function runExamPractice(container: HTMLElement, onDone: () => void): void {
  container.innerHTML = '';

  // 本畫面(清單)用一顆 escape 按鈕。進英文聽寫 / 整張考卷時,各畫面自帶
  // 一顆 escape,所以進那些畫面前先卸掉這顆、回清單時再 remount,避免同時兩顆 ✕。
  let unmountEscape: (() => void) | null = null;
  const mountOwnEscape = (): void => {
    if (unmountEscape) return;
    const u = mountEscapeButton(() => {
      if (unmountEscape) { unmountEscape(); unmountEscape = null; }
      onDone();
    });
    unmountEscape = u;
  };
  const dropOwnEscape = (): void => {
    if (unmountEscape) { unmountEscape(); unmountEscape = null; }
  };

  // 回到清單:重新 render 清單並 remount 本畫面的 escape。
  const back = (): void => {
    mountOwnEscape();
    renderList(container);
  };

  function renderList(host: HTMLElement): void {
    host.innerHTML = '';
    ensureAcademicTheme();

    // 紙底頁殼:serif 標題列 + 內容區。標題 / 副標 / list 維持既有 class(測試依賴)。
    const page = document.createElement('div');
    page.className = 'ef-page exam-practice';
    const inner = document.createElement('div');
    inner.className = 'ef-page__inner';
    page.appendChild(inner);

    const header = document.createElement('header');
    header.className = 'ef-page__header';
    const heading = document.createElement('div');
    heading.className = 'ef-page__heading';
    const h1 = document.createElement('h1');
    h1.className = 'ef-title exam-practice-title';
    h1.textContent = '真題練習';
    const sub = document.createElement('p');
    sub.className = 'ef-subtitle exam-practice-sub';
    sub.textContent = '選一份段考真卷,整張卷的題目一次看完,像真的考卷一樣作答。';
    heading.append(h1, sub);
    header.appendChild(heading);
    inner.appendChild(header);

    const loadingEl = document.createElement('div');
    loadingEl.className = 'exam-practice-loading';
    loadingEl.style.cssText = 'color:var(--ef-sub);';
    loadingEl.textContent = '載入題庫中…';
    inner.appendChild(loadingEl);

    const listEl = document.createElement('div');
    listEl.className = 'exam-practice-list';
    listEl.style.cssText = 'display:flex;flex-direction:column;gap:24px;';
    inner.appendChild(listEl);

    host.appendChild(page);

    void loadExamBanks().then((entries) => {
      loadingEl.remove();
      if (entries.length === 0) {
        listEl.innerHTML = '<div style="color:var(--ef-sub);">目前沒有可練習的題庫。</div>';
        return;
      }
      renderGroupedList(listEl, entries);
    });
  }

  function renderGroupedList(listEl: HTMLElement, entries: ExamPaperSummary[]): void {
    // 依科分組,科內保留載入順序(卷在前、聽寫項依序)。
    const bySubject = new Map<Subject, ExamPaperSummary[]>();
    for (const e of entries) {
      const arr = bySubject.get(e.subject) ?? [];
      arr.push(e);
      bySubject.set(e.subject, arr);
    }

    for (const subject of SUBJECT_ORDER) {
      const group = bySubject.get(subject);
      if (!group || group.length === 0) continue;

      const section = document.createElement('div');
      section.className = 'exam-practice-group';

      const heading = document.createElement('h2');
      heading.textContent = SUBJECT_LABEL[subject];
      heading.style.cssText =
        'margin:0 0 12px;font-family:var(--ef-serif);font-size:18px;font-weight:700;letter-spacing:0.5px;color:var(--ef-ink);border-bottom:1px solid var(--ef-line);padding-bottom:6px;';
      section.appendChild(heading);

      const cards = document.createElement('div');
      cards.style.cssText = 'display:flex;flex-direction:column;gap:12px;';
      section.appendChild(cards);

      for (const entry of group) {
        cards.appendChild(makeEntryCard(entry));
      }
      listEl.appendChild(section);
    }
  }

  function makeEntryCard(entry: ExamPaperSummary): HTMLElement {
    // 學術紙片卡(.ef-card,水平排:icon 點綴 + serif 標題 + 灰階說明)。
    const card =
      entry.kind === 'vocab-set'
        ? academicCard({
            icon: '🎧',
            label: entry.title,
            sub: `${entry.words.length} 個單字 · 聽寫練拼字`,
            onClick: () => startVocabSet(entry),
          })
        : academicCard({
            icon: '📄',
            label: entry.title,
            sub: `${entry.questions.length} 題 · 可批改 ${entry.questions.filter((q) => isAutoGraded(q.kind)).length} 題`,
            onClick: () => startPaper(entry),
          });
    // `.ef-card` 預設縱排;真題練習卡要 icon 與文字並排,加 class 套水平排版。
    card.classList.add('exam-practice-card', 'exam-practice-card--row');
    card.style.flexDirection = 'row';
    card.style.alignItems = 'center';
    return card;
  }

  function startVocabSet(entry: VocabSetEntry): void {
    // runDictation 自帶 escape;先卸掉本畫面的 escape,聽寫完再 remount。
    dropOwnEscape();
    container.innerHTML = '';
    runDictation(container, toDictationItems(entry.words), () => {
      back();
    });
  }

  function startPaper(entry: ExamPaperEntry): void {
    // renderExamPaper 自帶 escape;先卸掉本畫面的 escape,回清單時 back() 再 remount。
    dropOwnEscape();
    renderExamPaper(container, entry, () => {
      back();
    });
  }

  mountOwnEscape();
  renderList(container);
}
